<template>
  <dialog ref="dialogRef" class="played-dialog">
    <div class="played-card">
      <div class="played-title">{{ localize('handshake.played.question') }}</div>
      <HandshakePositionMark class="played-position" :position-index="position" />

      <div class="played-with">{{ localizeWithValues('handshake.played.with', [String(position), coplayer]) }}</div>

      <div class="played-actions">
        <button type="button" class="played-yes" :disabled="busy" @click="emit('answer', true)">
          {{ localize('handshake.played.yes') }}
        </button>
        <button type="button" class="played-no" :disabled="busy" @click="emit('answer', false)">
          {{ localize('handshake.played.no') }}
        </button>
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { localize, localizeWithValues } from '@/core/gambits';
import HandshakePositionMark from '@/views/main/handshakes/HandshakePositionMark.vue';
import { onBeforeUnmount, onMounted, ref } from 'vue';

defineProps<{
  position: number;
  coplayer: string;
  busy?: boolean;
}>();

/** Only whether it was played. Photos are added from the sheet, so the question is not read as asking for one. */
const emit = defineEmits<{ answer: [yes: boolean] }>();

const dialogRef = ref<HTMLDialogElement>();

const preventDismiss = (event: Event) => {
  event.preventDefault();
};

onMounted(() => {
  dialogRef.value?.showModal();
  dialogRef.value?.addEventListener('cancel', preventDismiss);
});

onBeforeUnmount(() => {
  dialogRef.value?.removeEventListener('cancel', preventDismiss);
  if (dialogRef.value?.open) {
    dialogRef.value.close();
  }
});
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

.played-dialog {
  width: 100%;
  height: 100%;
  border: 0;
  padding: var(--sai-top) 1rem var(--sai-bottom);
  margin: 0;
  max-width: 100%;
  max-height: 100%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  overflow: hidden;

  &::backdrop {
    background: rgba(23, 23, 23, 0.75);
  }
}

.played-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: min(100%, 420px);
  padding: var(--spacing-xxl) var(--spacing-xl);
  background: var(--color-dark-700);
  border: 1px solid var(--color-white-10);
  border-radius: var(--radius-large);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.played-title {
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  color: var(--color-white);
  margin-bottom: var(--spacing-xl);
}

.played-with {
  margin: 0 0 var(--spacing-xl);
  text-align: center;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--color-white);
}

.played-card :deep(.HandshakePositionMark) {
  width: 100%;
  max-width: none;
  margin-bottom: var(--spacing-md);
}

.played-card :deep(.gam-chessboard-piece) {
  width: 92%;
  height: 92%;
}

.played-actions {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.played-yes,
.played-no {
  width: 100%;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 2rem;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.played-yes {
  background: var(--color-linear-gradient);
  color: #fff;
  transition: opacity 200ms ease;

  @include hover {
    opacity: 0.9;
  }

  @include press;
}

.played-no {
  margin-top: var(--spacing-md);
  border: 1px solid var(--color-white-10);
  background: transparent;
  color: var(--color-white-70);
  transition: all 200ms ease;

  @include hover {
    background: var(--color-white-5);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press;
}
</style>
